import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  PlusOutlined,
  MinusOutlined,
  CompressOutlined,
} from '@ant-design/icons';

import { DevTrack, NodeStatus, PlanNode, TrackCode } from '../types';
import {
  DEFAULT_WORLD,
  Edge,
  LayoutMode,
  trackOrient,
  World,
  XY,
} from '../helpers';
import SkillEdges from './SkillEdges';
import SkillNodeCard from './SkillNodeCard';
import TrackLegend from './TrackLegend';

type Counts = Record<TrackCode, { unlocked: number; total: number }>;

type Props = {
  nodes: PlanNode[];
  tracks: DevTrack[];
  byCode: Record<string, PlanNode>;
  positions: Record<string, XY>;
  edges: Edge[];
  statusByCode: Record<string, NodeStatus>;
  activeNodeCodes: ReadonlySet<string>;
  trackColor: Record<TrackCode, string>;
  counts: Counts;
  selectedId: string | null;
  coreSelected: boolean;
  focusTrack: TrackCode | null;
  onFocus: (code: TrackCode | null) => void;
  onSelectNode: (code: string) => void;
  onSelectCore: () => void;
  onBackground: () => void;
  /** Layout world + mode. Optional: defaults to the legacy fixed cross world,
   *  so nothing changes for the four-track plans. */
  world?: World;
  layoutMode?: LayoutMode;
  /** Observer/preview affordance (ClientDashboard Studio): when set — at mount
   *  or on change — pan so this node (display `code`) sits centered in the
   *  viewport. The trainee view never passes it; undefined keeps the legacy
   *  hub-centered init untouched. */
  centerOnCode?: string | null;
};

const ZOOM_MIN = 0.2;
const ZOOM_MAX = 1.8;
const ZOOM_INIT = 1.2;

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, v));

const SkillTreeCanvas: React.FC<Props> = ({
  nodes,
  tracks,
  byCode,
  positions,
  edges,
  statusByCode,
  activeNodeCodes,
  trackColor,
  counts,
  selectedId,
  coreSelected,
  focusTrack,
  onFocus,
  onSelectNode,
  onSelectCore,
  onBackground,
  world = DEFAULT_WORLD,
  layoutMode = 'cross',
  centerOnCode,
}) => {
  const vpRef = useRef<HTMLDivElement>(null);
  const [tf, setTf] = useState({ tx: 0, ty: 0, zoom: ZOOM_INIT });
  const [hovered, setHovered] = useState<string | null>(null);
  // Hovering a legend track previews the isolation; a click (focusTrack, durable)
  // still locks it. Hover wins while it's set, then falls back to the click.
  const [hoverTrack, setHoverTrack] = useState<TrackCode | null>(null);
  const effectiveFocus = hoverTrack ?? focusTrack;
  // Debounce the CLEAR so hopping between legend items (a sub-frame gap over the
  // padding) doesn't flash the whole map back to full opacity. Entering a track
  // cancels any pending clear and applies immediately; leaving schedules a clear
  // that a fresh enter will cancel.
  const hoverClear = useRef<number | null>(null);
  const onHoverTrack = useCallback((code: TrackCode | null) => {
    if (hoverClear.current) {
      window.clearTimeout(hoverClear.current);
      hoverClear.current = null;
    }
    if (code !== null) setHoverTrack(code);
    else hoverClear.current = window.setTimeout(() => setHoverTrack(null), 90);
  }, []);
  useEffect(
    () => () => {
      if (hoverClear.current) window.clearTimeout(hoverClear.current);
    },
    [],
  );
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ moved: boolean; x: number; y: number } | null>(null);
  const initialized = useRef(false);

  const recenter = useCallback(
    (zoom: number) => {
      const vp = vpRef.current;
      if (!vp) return;
      setTf({
        zoom,
        tx: vp.clientWidth / 2 - world.center * zoom,
        ty: vp.clientHeight / 2 - world.center * zoom,
      });
    },
    [world.center],
  );

  // The zoom a reset / "Fit" lands on. Cross keeps the fixed ZOOM_INIT (its world
  // always frames the same way). octi is much larger and varies per plan, so it
  // fits the whole map to the viewport instead of opening zoomed into the hub.
  const resetZoom = useCallback((): number => {
    const vp = vpRef.current;
    if (layoutMode !== 'octi' || !vp || !vp.clientWidth) return ZOOM_INIT;
    // const fit =
    //   Math.min(vp.clientWidth / world.size, vp.clientHeight / world.size) *
    //   0.92;
    // return clamp(fit, ZOOM_MIN, ZOOM_INIT);
    return 0.6; // octi starting zoom (locked) — higher = more zoomed in
  }, [layoutMode, world.size]);

  // centerOnCode support: latest request + positions kept in refs so the
  // one-time init below can honor them without widening its dependency list.
  const centerReq = useRef<string | null | undefined>(centerOnCode);
  const positionsRef = useRef(positions);
  centerReq.current = centerOnCode;
  positionsRef.current = positions;

  const centerOnNode = useCallback(
    (code: string, zoom?: number): boolean => {
      const vp = vpRef.current;
      const pos = positionsRef.current[code];
      if (!vp || !pos || !vp.clientWidth) return false;
      setTf(prev => {
        const z = zoom ?? prev.zoom;
        // Positions are hub-centric (core at {0,0}); the world renders nodes at
        // world.center + pos — center THAT point, not the raw offset.
        return {
          zoom: z,
          tx: vp.clientWidth / 2 - (world.center + pos.x) * z,
          ty: vp.clientHeight / 2 - (world.center + pos.y) * z,
        };
      });
      return true;
    },
    [world.center],
  );

  // Center the world once the viewport has real dimensions. A ResizeObserver
  // is used (not a layout effect) because the parent sets the page height a
  // tick after mount — measuring too early yields a 0-height viewport.
  useEffect(() => {
    const vp = vpRef.current;
    if (!vp) return undefined;
    const ro = new ResizeObserver(() => {
      if (!initialized.current && vp.clientWidth > 0 && vp.clientHeight > 0) {
        initialized.current = true;
        // A pending centerOnCode wins over the default hub centering.
        const code = centerReq.current;
        if (!code || !centerOnNode(code, resetZoom())) recenter(resetZoom());
      }
    });
    ro.observe(vp);
    return () => ro.disconnect();
  }, [recenter, resetZoom, centerOnNode]);

  // Pan to a newly requested node after init (keeps the current zoom).
  useEffect(() => {
    if (!centerOnCode || !initialized.current) return;
    centerOnNode(centerOnCode);
  }, [centerOnCode, centerOnNode]);

  // Non-passive wheel listener so we can preventDefault and zoom to cursor.
  useEffect(() => {
    const vp = vpRef.current;
    if (!vp) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = vp.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setTf(prev => {
        const zoom = clamp(
          prev.zoom * Math.exp(-e.deltaY * 0.0015),
          ZOOM_MIN,
          ZOOM_MAX,
        );
        const k = zoom / prev.zoom;
        return {
          zoom,
          tx: cx - (cx - prev.tx) * k,
          ty: cy - (cy - prev.ty) * k,
        };
      });
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, []);

  // Global drag handlers (so panning continues outside the viewport).
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!drag.current) return;
      const dx = e.clientX - drag.current.x;
      const dy = e.clientY - drag.current.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) drag.current.moved = true;
      drag.current.x = e.clientX;
      drag.current.y = e.clientY;
      setTf(prev => ({ ...prev, tx: prev.tx + dx, ty: prev.ty + dy }));
    };
    const onUp = () => {
      const d = drag.current;
      drag.current = null;
      setDragging(false);
      // A click on empty canvas (no drag) clears the selection.
      if (d && !d.moved) onBackground();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [onBackground]);

  // Only fires for background mousedown — nodes/core stop propagation.
  const onCanvasMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    drag.current = { moved: false, x: e.clientX, y: e.clientY };
    setDragging(true);
  };

  const zoomBy = (factor: number) => {
    const vp = vpRef.current;
    if (!vp) return;
    const cx = vp.clientWidth / 2;
    const cy = vp.clientHeight / 2;
    setTf(prev => {
      const zoom = clamp(prev.zoom * factor, ZOOM_MIN, ZOOM_MAX);
      const k = zoom / prev.zoom;
      return { zoom, tx: cx - (cx - prev.tx) * k, ty: cy - (cy - prev.ty) * k };
    });
  };

  return (
    <div
      ref={vpRef}
      className={`devplan-canvas${dragging ? ' is-dragging' : ''}`}
      onMouseDown={onCanvasMouseDown}
    >
      <div
        className={`devplan-world${layoutMode === 'octi' ? ' is-octi' : ''}`}
        style={{
          width: world.size,
          height: world.size,
          transform: `translate(${tf.tx}px, ${tf.ty}px) scale(${tf.zoom})`,
        }}
      >
        <SkillEdges
          edges={edges}
          positions={positions}
          byCode={byCode}
          trackColor={trackColor}
          selectedId={selectedId}
          hoveredId={hovered}
          focusTrack={effectiveFocus}
          world={world}
          mode={layoutMode}
          orient={trackOrient(tracks)}
        />

        {/* Core node */}
        <button
          type="button"
          className={`devplan-core${coreSelected ? ' is-selected' : ''}`}
          style={{ left: world.center, top: world.center }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            onSelectCore();
          }}
          aria-label="Core"
        >
          {/* North-star emblem — the goal the four tracks converge toward. */}
          <svg
            className="devplan-core__art"
            viewBox="0 0 100 100"
            aria-hidden="true"
          >
            <path d="M50 7 C51 40 60 49 93 50 C60 51 51 60 50 93 C49 60 40 51 7 50 C40 49 49 40 50 7 Z" />
            <circle className="devplan-core__art-dot" cx="50" cy="50" r="3.4" />
          </svg>
        </button>

        {/* Skill nodes */}
        {nodes.map(node => {
          const pos = positions[node.code];
          if (!pos) return null;
          const faded = effectiveFocus
            ? node.track_code !== effectiveFocus
            : false;
          return (
            <div
              key={node.code}
              className="devplan-node-pos"
              style={{ left: world.center + pos.x, top: world.center + pos.y }}
            >
              <SkillNodeCard
                node={node}
                status={statusByCode[node.code]}
                inProgress={activeNodeCodes.has(node.code)}
                color={trackColor[node.track_code]}
                selected={selectedId === node.code}
                faded={faded}
                onSelect={onSelectNode}
                onHover={setHovered}
              />
            </div>
          );
        })}
      </div>

      <TrackLegend
        tracks={tracks}
        counts={counts}
        focusTrack={focusTrack}
        onFocus={onFocus}
        onHover={onHoverTrack}
      />

      <div className="devplan-zoomctl">
        <button type="button" onClick={() => zoomBy(1.2)} title="Zoom in">
          <PlusOutlined />
        </button>
        <button type="button" onClick={() => zoomBy(1 / 1.2)} title="Zoom out">
          <MinusOutlined />
        </button>
        <button type="button" onClick={() => recenter(resetZoom())} title="Fit">
          <CompressOutlined />
        </button>
      </div>

      <div className="devplan-hint">Drag to pan · scroll to zoom</div>
    </div>
  );
};

export default SkillTreeCanvas;
